import { useState, useRef, useEffect } from "react";
import { cn } from "../../lib/utils";
import * as Dialog from "@radix-ui/react-dialog";
import type { Owner } from "../../data/types";
import { OWNER_OPTIONS } from "../../lib/owner";
import { useCurrentOwner } from "../../hooks/useCurrentOwner";

interface NewFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string, owner: Owner) => void;
}

export function NewFolderDialog({ open, onOpenChange, onSubmit }: NewFolderDialogProps) {
  const { owner: currentOwner } = useCurrentOwner();
  const [name, setName] = useState("");
  const [owner, setOwner] = useState<Owner>(currentOwner);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setOwner(currentOwner);
      // Delay focus to after animation
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, currentOwner]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    onSubmit(trimmed, owner);
    onOpenChange(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/50 backdrop-blur-[2px]",
            "data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out",
          )}
        />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
            "w-full max-w-[400px] rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-surface)] p-[var(--spacing-6)]",
            "shadow-lg",
            "data-[state=open]:animate-page-enter",
            "focus:outline-none",
          )}
        >
          <Dialog.Title className="text-[16px] font-semibold text-[var(--color-text-primary)]">
            New Folder
          </Dialog.Title>
          <Dialog.Description className="text-[13px] text-[var(--color-text-muted)] mt-[var(--spacing-1)]">
            Create a folder to organize your assets by project.
          </Dialog.Description>

          <form onSubmit={handleSubmit} className="mt-[var(--spacing-4)] flex flex-col gap-[var(--spacing-3)]">
            {/* Name */}
            <div className="flex flex-col gap-[var(--spacing-1)]">
              <label
                htmlFor="folder-name"
                className="text-[12px] font-medium text-[var(--color-text-muted)] uppercase tracking-[0.06em]"
              >
                Folder name
              </label>
              <input
                ref={inputRef}
                id="folder-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Project Alpha"
                className={cn(
                  "h-[36px] px-[var(--spacing-3)] rounded-[var(--radius-md)]",
                  "border border-[var(--color-border-stroke)] bg-[var(--color-bg-canvas)]",
                  "text-[14px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]",
                  "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)] focus:ring-offset-2",
                )}
              />
            </div>

            {/* Owner */}
            <div className="flex flex-col gap-[var(--spacing-1)]">
              <label
                htmlFor="folder-owner"
                className="text-[12px] font-medium text-[var(--color-text-muted)] uppercase tracking-[0.06em]"
              >
                Owner
              </label>
              <select
                id="folder-owner"
                value={owner}
                onChange={(e) => setOwner(e.target.value as Owner)}
                className={cn(
                  "h-[36px] px-[var(--spacing-2)] rounded-[var(--radius-md)]",
                  "border border-[var(--color-border-stroke)] bg-[var(--color-bg-canvas)]",
                  "text-[14px] text-[var(--color-text-primary)]",
                  "focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-focus)]",
                  "cursor-pointer",
                )}
              >
                {OWNER_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-[var(--spacing-2)] mt-[var(--spacing-2)]">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className={cn(
                    "h-[32px] px-[var(--spacing-3)] text-[13px] font-medium",
                    "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]",
                    "rounded-[var(--radius-md)] transition-colors cursor-pointer",
                  )}
                >
                  Cancel
                </button>
              </Dialog.Close>
              <button
                type="submit"
                disabled={!name.trim()}
                className={cn(
                  "h-[32px] px-[var(--spacing-4)] text-[13px] font-medium text-white",
                  "rounded-[var(--radius-md)]",
                  "bg-gradient-to-b from-[var(--color-accent-400)] to-[var(--color-accent-600)]",
                  "hover:from-[var(--color-accent-300)] hover:to-[var(--color-accent-500)]",
                  "disabled:opacity-40 disabled:cursor-not-allowed",
                  "transition-all cursor-pointer",
                )}
              >
                Create
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
